import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Fade,
  Grow,
  TextField,
} from "@mui/material";
import {
  CheckCircle,
  Error,
  Email,
  ArrowForward,
} from "@mui/icons-material";
import ROUTES from "../../router/routesModel";
import { verifyEmail, requestVerificationEmail } from "../service/userApi";
import { claimRefusals } from "../../utils/refusalNotice";
import { supabase, isSupabaseConfigured } from "../../lib/supabaseClient";
import { buildAuthRedirectUrl } from "../../lib/nativeAuth";
import { dvh } from "../../styles/viewportHeight";

// What an ACCEPTED resend may say, on either path below. Neither backend tells
// us whether the address has an account waiting: ours answers this sentence on
// every branch by design, and Supabase's /resend answers 200 and sends nothing
// for an unknown or already-confirmed address. So no more than this is known.
const RESEND_ACCEPTED =
  "If that address has an unverified account, a new verification email is on its way.";

const EmailVerificationPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendEmail, setResendEmail] = useState("");

  useEffect(() => {
    const verify = async () => {
      try {
        const result = await verifyEmail(token);
        setStatus("success");
        setMessage(result.message || "Email verified successfully!");
        setTimeout(() => {
          navigate(ROUTES.LOGIN);
        }, 4000);
      } catch (error) {
        setStatus("error");
        setMessage(
          error.toString() ||
            "Email verification failed. The link may be invalid or expired."
        );
      }
    };

    if (token) {
      verify();
      return;
    }
    // No token: this is /verify-email, the tokenless door for someone whose
    // email never arrived. There is nothing to verify, so go straight to the
    // branch that carries the resend form rather than spinning on "Verifying
    // Your Email" forever — which is what this did before the route existed.
    setStatus("error");
    setMessage(
      "Enter the email address you signed up with and we'll send a new verification link."
    );
  }, [token, navigate]);

  /**
   * P1-8 (pre-launch audit, 2026-09-26): "Resend" said it sent an email when it
   * didn't. With Supabase configured — every shipping build — sign-in IS
   * Supabase (handleLogin in useHandleUsers.js never falls back to the legacy
   * login), so what stands between this visitor and the app is a Supabase
   * identity whose confirmation email Supabase owns. The legacy route cannot
   * reach it: the server creates the Mongo row only from a session, and
   * refuses the session until the email is confirmed
   * (SUPABASE_SESSION_EMAIL_UNCONFIRMED in usersRestController.js). So it found
   * nobody, sent nothing, and answered "on its way" anyway. A legacy row with
   * the same address fared no better: its /verify-email link flips Mongo's
   * flag while Supabase goes on answering "Email not confirmed".
   *
   * Never throws; reports every outcome through `message`.
   */
  const resendWithSupabase = async (email) => {
    let refusal = null;
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email,
        // The landing signUp asked for, so the new link finishes the way the
        // first one would have — the custom scheme on native (#83).
        options: { emailRedirectTo: buildAuthRedirectUrl(ROUTES.AUTH_CALLBACK) },
      });
      refusal = error;
    } catch (thrown) {
      // supabase-js returns auth failures but rethrows anything else.
      refusal = thrown || {};
    }
    if (!refusal) {
      setMessage(RESEND_ACCEPTED);
      return;
    }
    // A 4xx carries Supabase's own sentence, written for the user. The one
    // that matters is the rate limit (429: "For security purposes, you can
    // only request this after 42 seconds."), which says when to try again.
    // Showing it reveals nothing the public /resend endpoint does not already
    // tell anyone holding the anon key that ships in this bundle. Anything
    // else — no network (status 0), a 5xx, a throw — arrives as a platform
    // string like "Failed to fetch", so it gets plain words instead.
    setMessage(
      `No email was sent. ${
        refusal.status >= 400 && refusal.status < 500 && refusal.message
          ? refusal.message
          : "The request did not go through. Check your connection and try again."
      }`
    );
  };

  const handleResend = async () => {
    // The token on this page has already failed, so it tells us nothing about
    // whose account this is — and this visitor is signed out by definition
    // (an unverified account cannot log in). So the address has to be typed.
    // Previously this called the signed-in endpoint with no address at all and
    // answered 401, which left the account unreachable by its own owner.
    const email = resendEmail.trim();
    if (!email) {
      setMessage("Enter the email address you signed up with.");
      return;
    }
    setIsResending(true);
    try {
      // Same test handleLogin uses to pick Supabase sign-in, so the resend
      // always targets the identity that sign-in will check. The legacy route
      // stays for builds without Supabase, where sign-in is the legacy login.
      if (isSupabaseConfigured() && supabase) {
        await resendWithSupabase(email);
        return;
      }
      const result = await requestVerificationEmail(email);
      // Deliberately does NOT move `status` to "success". That branch is the
      // *verified* screen — a green tick, "Email Verified Successfully!" and
      // "Redirecting you to login page..." — and none of it is true here: the
      // link on this page still failed, nothing has been verified, and the
      // 4s redirect timer lives in `verify()` alone, so the promised redirect
      // never fires. Staying on the error branch keeps the real heading and
      // puts the sent-confirmation in the same `{message}` slot under it,
      // directly above the Resend and Go-to-Login buttons.
      // The server's reply is deliberately the same whether or not that
      // address has an unverified account, so this must not be reworded into
      // anything that claims one was found.
      setMessage(result.message || RESEND_ACCEPTED);
    } catch (error) {
      // Shown on the page below the button (B-31).
      claimRefusals();
      setMessage(error.toString() || "Failed to resend verification email.");
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigate(ROUTES.LOGIN);
  };

  return (
    <Box
      sx={{
        minHeight: dvh(100),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "radial-gradient(circle at 35% 45%, rgba(120, 119, 198, 0.3), transparent 50%), radial-gradient(circle at 65% 75%, rgba(138, 43, 226, 0.3), transparent 50%)",
          animation: "pulse 8s ease-in-out infinite",
        },
        "@keyframes pulse": {
          "0%, 100%": {
            opacity: 1,
          },
          "50%": {
            opacity: 0.8,
          },
        },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 550, px: 2, position: "relative", zIndex: 1 }}>
        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(20px)",
              boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                py: 5,
                px: 4,
                textAlign: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -1,
                  left: 0,
                  right: 0,
                  height: 2,
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)",
                },
              }}
            >
              <Grow in timeout={1000}>
                <Email sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
              </Grow>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                }}
              >
                Email Verification
              </Typography>
            </Box>

            {/* Content Section */}
            <Box sx={{ p: 5, textAlign: "center" }}>
              {status === "verifying" && (
                <Fade in timeout={600}>
                  <Box>
                    <CircularProgress
                      size={60}
                      sx={{
                        mb: 3,
                        color: "primary.main",
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Verifying Your Email
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Please wait while we confirm your email address...
                    </Typography>
                  </Box>
                </Fade>
              )}

              {status === "success" && (
                <Fade in timeout={600}>
                  <Box>
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 3,
                        animation: "scaleIn 0.6s ease-out",
                        "@keyframes scaleIn": {
                          from: { transform: "scale(0)" },
                          to: { transform: "scale(1)" },
                        },
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 60, color: "white" }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                      Email Verified Successfully!
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6 }}
                    >
                      {message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Redirecting you to login page...
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleGoToLogin}
                      endIcon={<ArrowForward />}
                      sx={{
                        py: 1.5,
                        px: 4,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        boxShadow: "0 4px 15px 0 rgba(102, 126, 234, 0.4)",
                        transition: "all 0.3s",
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 6px 25px 0 rgba(102, 126, 234, 0.6)",
                        },
                      }}
                    >
                      Go to Login
                    </Button>
                  </Box>
                </Fade>
              )}

              {status === "error" && (
                <Fade in timeout={600}>
                  <Box>
                    <Box
                      sx={{
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 3,
                        animation: "shake 0.5s ease-out",
                        "@keyframes shake": {
                          "0%, 100%": { transform: "translateX(0)" },
                          "25%": { transform: "translateX(-10px)" },
                          "75%": { transform: "translateX(10px)" },
                        },
                      }}
                    >
                      <Error sx={{ fontSize: 60, color: "white" }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                      Verification Failed
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 4, lineHeight: 1.6 }}
                    >
                      {message}
                    </Typography>
                    {/* The failed token identifies nobody, and this visitor is
                        signed out, so Resend needs the address typed. */}
                    <TextField
                      fullWidth
                      type="email"
                      label="Email you signed up with"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      disabled={isResending}
                      inputProps={{
                        "data-testid": "resend-email-input",
                        autoCapitalize: "none",
                        autoCorrect: "off",
                      }}
                      sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                      <Button
                        variant="contained"
                        onClick={handleResend}
                        disabled={isResending || !resendEmail.trim()}
                        data-testid="resend-email-button"
                        sx={{
                          py: 1.2,
                          px: 3,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        {isResending ? "Sending..." : "Resend Email"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleGoToLogin}
                        sx={{
                          py: 1.2,
                          px: 3,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          borderWidth: 2,
                          "&:hover": {
                            borderWidth: 2,
                          },
                        }}
                      >
                        Go to Login
                      </Button>
                    </Box>
                  </Box>
                </Fade>
              )}
            </Box>

            {/* Footer Wave */}
            <Box
              sx={{
                height: 8,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            />
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
};

export default EmailVerificationPage;
